/**
 * Call History Card List Component (Presentation)
 *
 * Scrollable list of call history cards for the queue panel History tab.
 * Fetches a limited set of recent call history and displays compact cards.
 */

import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';

import { PhoneCallSummaryModal } from '@/modules/queue';
import type { QueueModel } from '@/modules/queue';

import { useCallHistorySearch } from '../../application/hooks';
import type { CallHistorySearchRequest } from '../../domain/types/requests';
import type { CallHistoryItem } from '../../domain/types/responses';

import { CallHistoryCard } from './CallHistoryCard';
import { convertCallHistoryToQueueModel } from '../utils/callHistoryToQueueModel';

const HISTORY_PAGE_SIZE = 15;

const defaultHistoryRequest: CallHistorySearchRequest = {
  page: 1,
  per_page: HISTORY_PAGE_SIZE,
  filters: [],
  sorts: [{ column: 'call_time_start', order: 'desc' }],
};

const SUMMARY_OUTCOME = 'Pending Confirmation';

export function CallHistoryCardList() {
  const navigate = useNavigate();

  const request = useMemo(() => defaultHistoryRequest, []);
  const { data: searchResponse, isLoading, error } = useCallHistorySearch(request);

  const items = searchResponse?.items ?? [];

  // Summary modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedQueueData, setSelectedQueueData] = useState<QueueModel | null>(null);

  const handleCardClick = useCallback(
    (item: CallHistoryItem) => {
      if (item.call_outcome === SUMMARY_OUTCOME) {
        const queueModel = convertCallHistoryToQueueModel(item);
        setSelectedQueueData(queueModel);
        setModalOpen(true);
      } else if (item.case_id != null) {
        navigate(`/case-detail/${String(item.case_id)}`);
      }
    },
    [navigate]
  );

  const handleModalClose = useCallback(() => {
    setModalOpen(false);
    setSelectedQueueData(null);
  }, []);

  if (isLoading) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Typography variant="body2" color="error" textAlign="center">
          Error loading call history
        </Typography>
        <Typography variant="caption" color="text.secondary" textAlign="center">
          {error instanceof Error ? error.message : 'Unknown error'}
        </Typography>
      </Box>
    );
  }

  if (items.length === 0) {
    return (
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No call history
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 1.5,
          display: 'flex',
          flexDirection: 'column',
          gap: 1,
        }}
      >
        {items.map(item => (
          <CallHistoryCard key={item.id ?? `${String(item.case_id)}-${String(item.call_time_start)}`} item={item} onClick={() => handleCardClick(item)} />
        ))}
      </Box>

      <PhoneCallSummaryModal open={modalOpen} onClose={handleModalClose} queueData={selectedQueueData} />
    </>
  );
}
